
import { Grid } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../../../contexts/LanguageContext';
import Drawer, { IDefaultDrawerProps } from '../../../../components/Drawer';
import { ActionButton } from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import SelectField from '../../../../components/SelectField';
import FormHelper from '../../../../helpers/FormHelper';
import { RegistrationStatusData, RegistrationStatusEnum } from '../../../../data/Enums';
import IEnterprise from '../../../../interfaces/IEnterprise';
import { useAuthContext } from '../../../../contexts/AuthContext';
import useState from '../../../../hooks/useState';


interface IUserFilterProps extends IDefaultDrawerProps {
    handleApplyFilters: (formData: FormData) => void;
    enterprises: IEnterprise[];
}

interface IStateValues {
    fullName: string;
    login: string;
    email: string;
    enterprise: number[];
    registrationStatus: number[];
}


const Filters: React.FC<IUserFilterProps> = ({
    open,
    handleClose,
    handleApplyFilters,
    enterprises
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();

    const state = useState<IStateValues>({
        fullName: '',
        login: '',
        email: '',
        enterprise: enterprise ? [enterprise.id] : [],
        registrationStatus: [RegistrationStatusEnum.Active, RegistrationStatusEnum.Inactive]
    });


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const formData = FormHelper.Create(event);
        handleApplyFilters(formData);
        handleClose();
        // eslint-disable-next-line
    }, []);


    const footer = useMemo(() => {
        return <ActionButton
            action='apply'
            fullWidth={true}
            className='text-alwaysshow'
            type='submit'
        />;
        // eslint-disable-next-line
    }, [translate]);
    

    return (
        <Drawer
            anchor='right'
            open={open}
            handleClose={handleClose}
            title={translate('filter')}
            footer={footer}
            onSubmit={handleSubmit}
        >
            <Grid container>
                <Grid item xs={12}>
                    <TextField 
                        label={translate('fullName')} 
                        name='fullName' 
                        value={state.values.fullName} 
                        onChange={e => state.update(e.target.name, e.target.value)}
                        required={false}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField 
                        label={translate('user')} 
                        name='login' 
                        value={state.values.login} 
                        onChange={e => state.update(e.target.name, e.target.value)}
                        required={false}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField 
                        label={translate('email')} 
                        name='email' 
                        value={state.values.email} 
                        onChange={e => state.update(e.target.name, e.target.value)}
                        required={false}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('enterprises')}
                        name='enterprise'
                        value={state.values.enterprise.length > 0 ? state.values.enterprise : [0]}
                        multiple={true}
                        required={false}
                        onChange={e => {
                            const value = e.target.value as number[];
                            state.update(e.target.name, state.values.enterprise.length > 0 && value.includes(0) ? [] : value.filter(v => v !== 0));
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprises.map(e => {
                            return {
                                value: +e.id,
                                text: e.name
                            };
                        }))}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <SelectField
                        label={translate('situation')}
                        name='registrationStatus'
                        value={state.values.registrationStatus}
                        multiple={true}
                        required={true}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={Object.values(RegistrationStatusData).filter(s => s.visible).map(s => {
                            return {
                                value: s.id,
                                text: translate(s.name)
                            };
                        })}
                    />
                </Grid>
            </Grid>
        </Drawer>
    );
}

export default Filters;